/**
 * useCompareCart — a small property-id list the visitor has queued for the
 * /compare page.
 *
 * Anonymous visitors accumulate items in localStorage. When they sign in
 * (via ShopperAuthContext) we union-merge the local list into the server
 * copy so the compare queue survives across devices/browsers. Once signed
 * in, the server is the source of truth and mutations write through to
 * both server and local (so the tray stays snappy without an extra round
 * trip on every toggle).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { useShopperAuth } from "@/context/ShopperAuthContext";

const API = getApiUrl();
const STORAGE_KEY = "slh.compare.v1";
export const COMPARE_MAX = 3;

function readLocal() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function writeLocal(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    // Broadcast so every subscribed hook instance stays in sync.
    window.dispatchEvent(new CustomEvent("slh:compare-changed"));
  } catch { /* quota — ignore */ }
}

function snapshotOf(property) {
  if (!property || typeof property !== "object") return null;
  return {
    property_id: property.property_id,
    title: property.title,
    display_address: property.display_address,
    price: property.price,
    currency: property.currency,
    gallery: (property.gallery || []).slice(0, 1),
  };
}

export function useCompareCart() {
  const { shopper } = useShopperAuth();
  const [items, setItems] = useState([]);
  const syncedForShopper = useRef(null);

  const refresh = useCallback(async () => {
    if (!shopper) {
      setItems(readLocal());
      return;
    }
    try {
      const { data } = await axios.get(`${API}/shopper/me/compare`, { withCredentials: true });
      const serverItems = (data.items || []).map((r) => ({
        property_id: r.property_id,
        property: r.property || null,
        created_at: r.created_at,
      }));
      setItems(serverItems);
      // Mirror to local so the tray still renders instantly on page reload
      // before the /me/compare fetch resolves.
      writeLocalOnly(serverItems);
    } catch {
      // Fall back to local on network error so we don't blank the tray.
      setItems(readLocal());
    }
  }, [shopper]);

  // Same as writeLocal() but without firing the change event — used when the
  // server response is what triggered the state update, so we don't need a
  // second re-render.
  function writeLocalOnly(list) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* noop */ }
  }

  // Sign-in path: union-merge local → server once per shopper session, then
  // refresh from the server (source of truth thereafter).
  useEffect(() => {
    if (!shopper) {
      syncedForShopper.current = null;
      // Anonymous → reset to local snapshot.
      setItems(readLocal());
      return;
    }
    if (syncedForShopper.current === shopper.shopper_id) return;
    const local = readLocal();
    const ids = local.map((x) => x.property_id).filter(Boolean);
    (async () => {
      if (ids.length > 0) {
        try {
          await axios.post(
            `${API}/shopper/me/compare/sync`,
            { property_ids: ids },
            { withCredentials: true },
          );
        } catch { /* ignore — refresh() will still surface server items */ }
      }
      syncedForShopper.current = shopper.shopper_id;
      await refresh();
    })();
  }, [shopper, refresh]);

  // Cross-instance change listener — every mounted hook stays in sync via
  // the synthetic `slh:compare-changed` event we emit from writeLocal().
  useEffect(() => {
    const onChange = () => setItems(readLocal());
    window.addEventListener("slh:compare-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("slh:compare-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const set = new Set(items.map((i) => i.property_id));
  const isInCompare = (propertyId) => set.has(propertyId);

  const toggle = useCallback((property) => {
    const propertyId = typeof property === "string" ? property : property?.property_id;
    if (!propertyId) return { in: false, full: false };
    const current = readLocal();
    const currentlyIn = current.some((i) => i.property_id === propertyId);

    if (currentlyIn) {
      const next = current.filter((i) => i.property_id !== propertyId);
      writeLocal(next);
      setItems(next);
      if (shopper) {
        axios.delete(`${API}/shopper/me/compare/${propertyId}`, { withCredentials: true })
          .catch(() => { /* swallow — local write already succeeded */ });
      }
      return { in: false, full: false };
    }
    if (current.length >= COMPARE_MAX) {
      return { in: false, full: true };
    }
    const snap = snapshotOf(typeof property === "object" ? property : null);
    const next = [...current, {
      property_id: propertyId,
      property: snap,
      created_at: new Date().toISOString(),
    }];
    writeLocal(next);
    setItems(next);
    if (shopper) {
      axios.post(
        `${API}/shopper/me/compare`,
        { property_id: propertyId },
        { withCredentials: true },
      ).catch(() => { /* swallow — local write already succeeded */ });
    }
    return { in: true, full: false };
  }, [shopper]);

  const clear = useCallback(() => {
    writeLocal([]);
    setItems([]);
    if (shopper) {
      axios.delete(`${API}/shopper/me/compare`, { withCredentials: true }).catch(() => {});
    }
  }, [shopper]);

  const remove = useCallback((propertyId) => {
    const next = readLocal().filter((i) => i.property_id !== propertyId);
    writeLocal(next);
    setItems(next);
    if (shopper) {
      axios.delete(`${API}/shopper/me/compare/${propertyId}`, { withCredentials: true }).catch(() => {});
    }
  }, [shopper]);

  return { items, isInCompare, toggle, remove, clear, max: COMPARE_MAX };
}
